import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Chevrons from "../components/Chevrons";
import { BRAND_ORANGE } from "../components/BaseLogo";
import {
  services,
  process,
  heroImages,
  completedProjects,
  brochureUrl,
} from "../mock";

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const Hero = () => (
  <section className="relative h-screen min-h-[640px] w-full overflow-hidden bg-[#0f0f10]">
    <div
      className="absolute inset-0 bg-cover bg-center scale-105"
      style={{ backgroundImage: `url(${heroImages.home})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] via-[#0f0f10]/70 to-[#0f0f10]/50" />
    <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24">
      <h1 className="hero-title text-white text-[54px] md:text-[110px] leading-[0.95] font-medium tracking-[-0.02em]">
        Your vision, our passion.
      </h1>
      <p className="mt-6 text-white/70 text-[15px] md:text-[17px] tracking-wide max-w-md">
        Principal contractors specialising in one-off and bespoke build
        projects throughout Devon and Cornwall.
      </p>
      <div className="mt-9 flex flex-wrap items-center gap-4">
        <Link
          to="/contact"
          onClick={() => window.scrollTo({ top: 0 })}
          className="inline-flex w-fit items-center px-8 py-4 text-[11px] tracking-[0.24em] uppercase text-white transition-all duration-300 hover:brightness-110"
          style={{ backgroundColor: BRAND_ORANGE }}
        >
          Get in Touch
        </Link>
        <a
          href={brochureUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-2 px-8 py-4 text-[11px] tracking-[0.24em] uppercase border border-white/70 text-white hover:bg-white hover:text-[#0f0f10] transition-all duration-300"
        >
          View Brochure
          <ArrowRight size={15} />
        </a>
      </div>
    </div>
  </section>
);

const Services = () => {
  const [big, ...rest] = services;
  return (
    <section id="services" className="bg-[#e9e4dc] pt-16 md:pt-24 pb-12 md:pb-20">
     <div className="max-w-[1500px] mx-auto px-6 md:px-10">
      {/* Section eyebrow */}
      <div data-reveal className="reveal flex items-center gap-3 mb-8">
        <span className="text-[11px] tracking-[0.28em] uppercase text-[#0f0f10]/60">
          What We Do
        </span>
        <Chevrons size={16} />
      </div>

      {/* Large feature */}
      <div className="relative group overflow-hidden h-[62vh] min-h-[440px]">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-105"
          style={{ backgroundImage: `url(${big.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f10]/85 via-[#0f0f10]/40 to-transparent" />
        <div className="relative h-full px-8 md:px-14 flex flex-col justify-center">
          <div data-reveal className="reveal max-w-lg">
            <h3 className="text-white text-[34px] md:text-[42px] font-medium leading-tight">
              {big.title}
            </h3>
            <p className="mt-5 text-white/75 text-[15px] leading-relaxed">
              {big.body}
            </p>
            <Link
              to={big.ctaLink}
              onClick={() => window.scrollTo({ top: 0 })}
              className="mt-7 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase group/link"
              style={{ color: BRAND_ORANGE }}
            >
              {big.cta}
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover/link:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Secondary services */}
      <div className="grid gap-[3px] mt-[3px] sm:grid-cols-2">
        {rest.map((s, i) => (
          <div
            key={s.id}
            className={`relative group overflow-hidden h-[46vh] min-h-[360px] ${
              i === rest.length - 1 && rest.length % 2 === 1 ? "sm:col-span-2" : ""
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-105"
              style={{ backgroundImage: `url(${s.image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] via-[#0f0f10]/70 to-[#0f0f10]/15" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <h3 className="text-white text-[26px] md:text-[30px] font-medium leading-tight">
                {s.title}
              </h3>
              {s.tagline && (
                <p
                  className="mt-2 text-[13px] font-semibold"
                  style={{ color: BRAND_ORANGE }}
                >
                  {s.tagline}
                </p>
              )}
              <p className="mt-2 text-white/70 text-[13.5px] leading-relaxed max-w-md">
                {s.body}
              </p>
            </div>
          </div>
        ))}
      </div>
     </div>
    </section>
  );
};

const Contractor = () => (
  <section className="relative overflow-hidden bg-[#0f0f10]">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-30"
      style={{ backgroundImage: `url(${heroImages.contractor})` }}
    />
    <div className="absolute inset-0 bg-[#0f0f10]/50" />
    <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-32 md:py-40 text-center">
      <p
        data-reveal
        className="reveal text-[11px] tracking-[0.32em] uppercase font-semibold"
        style={{ color: BRAND_ORANGE }}
      >
        A Different Kind Of Contractor
      </p>
      <h2
        data-reveal
        className="reveal delay-1 mt-8 text-white text-[36px] md:text-[62px] leading-[1.05] font-medium"
      >
        We don't just build spaces.
        <br />
        We bring your vision to life.
      </h2>
      <p
        data-reveal
        className="reveal delay-2 mt-8 text-white/65 text-[16px] leading-relaxed max-w-2xl mx-auto"
      >
        Base are specialists in delivering exceptional one-off homes and
        bespoke build projects across Devon and Cornwall.
      </p>
      <Link
        data-reveal
        to="/contact"
        onClick={() => window.scrollTo({ top: 0 })}
        className="reveal delay-3 mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase group"
        style={{ color: BRAND_ORANGE }}
      >
        Start Your Journey
        <ArrowRight
          size={15}
          className="transition-transform duration-300 group-hover:translate-x-1"
        />
      </Link>
    </div>
  </section>
);

const ProjectsPreview = () => {
  const items = [...completedProjects].filter((p) => p.image).slice(0, 9);

  return (
    <section className="relative bg-[#0f0f10]">
      {/* Banner */}
      <div className="relative h-[70vh] min-h-[460px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImages.projects})` }}
        />
        <div className="absolute inset-0 bg-[#0f0f10]/60" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <Chevrons size={22} className="mb-4" />
          <h2 className="text-white text-[46px] md:text-[76px] font-medium">Projects.</h2>
          <p className="mt-5 text-white/70 text-[16px] max-w-xl">
            A select portfolio of one-off homes and bespoke build projects
            delivered across Devon and Cornwall.
          </p>
          <Link
            to="/projects"
            onClick={() => window.scrollTo({ top: 0 })}
            className="mt-9 inline-flex items-center px-8 py-4 text-[11px] tracking-[0.24em] uppercase border border-white/70 text-white hover:bg-white hover:text-[#0f0f10] transition-all duration-300"
          >
            View All Projects
          </Link>
        </div>
      </div>

      {/* Thumbnails */}
      <div className="grid grid-cols-2 md:grid-cols-3">
        {items.map((p) => (
          <Link
            key={p.id}
            to="/projects"
            onClick={() => window.scrollTo({ top: 0 })}
            className="relative group overflow-hidden aspect-[4/3]"
          >
            <img
              src={p.image}
              alt={p.name}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-[#0f0f10]/30 group-hover:bg-[#0f0f10]/55 transition-colors duration-500" />
            <div className="absolute bottom-0 left-0 p-6 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
              <p className="text-white text-[20px] font-light">{p.name}</p>
              <p className="text-white/70 text-[12px] tracking-[0.18em] uppercase mt-1">
                {p.location}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const HowWeWork = () => (
  <section className="bg-[#ded7cb]">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-28">
      <h2
        data-reveal
        className="reveal text-[#0f0f10] text-[44px] md:text-[64px] font-light"
      >
        How we work.
      </h2>
      <div className="mt-16 border-t border-black/15">
        <div className="grid md:grid-cols-4 gap-y-12 md:gap-x-10 pt-12">
          {process.map((step, i) => (
            <div
              key={step.num}
              data-reveal
              className={`reveal delay-${i} md:border-r md:last:border-r-0 border-black/10 md:pr-8`}
            >
              <span
                className="text-[13px] tracking-[0.2em] font-bold"
                style={{ color: BRAND_ORANGE }}
              >
                {step.num}
              </span>
              <h3 className="mt-5 text-[#0f0f10] text-[24px] font-medium">
                {step.title}
              </h3>
              <p className="mt-4 text-[#0f0f10]/65 text-[14px] leading-relaxed">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  useReveal();
  return (
    <div>
      <Hero />
      <Services />
      <Contractor />
      <div className="bg-[#e9e4dc] h-12 md:h-20" />
      <ProjectsPreview />
      <HowWeWork />
    </div>
  );
};

export default Home;
